import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Plus, RefreshCw, Search, Edit, Trash2, Phone } from 'lucide-react'
import { toast } from 'react-toastify'
import { phoneNumbersAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/LoadingSpinner'
import PhoneNumberModal from '../components/PhoneNumberModal'
import ConfirmDialog from '../components/ConfirmDialog'

function PhoneNumbers() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedPhone, setSelectedPhone] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deletePhone, setDeletePhone] = useState(null)
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: phoneNumbers, isLoading } = useQuery('phone-numbers', phoneNumbersAPI.getPhoneNumbers)
  
  const syncMutation = useMutation(phoneNumbersAPI.syncPhoneNumbers, {
    onSuccess: (data) => {
      toast.success(data.data.message)
      queryClient.invalidateQueries('phone-numbers')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Sync failed')
    }
  })

  const deleteMutation = useMutation(phoneNumbersAPI.deletePhoneNumber, {
    onSuccess: () => {
      toast.success('Phone number deleted successfully')
      queryClient.invalidateQueries('phone-numbers')
      setDeletePhone(null)
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Delete failed')
    }
  })

  const filteredPhones = phoneNumbers?.data?.phoneNumbers?.filter(phone =>
    phone.phoneNumber.includes(searchTerm) ||
    phone.carrierName?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  const handleEdit = (phone) => {
    setSelectedPhone(phone)
    setIsModalOpen(true)
  }

  const handleCreate = () => {
    setSelectedPhone(null)
    setIsModalOpen(true)
  }

  const handleDelete = (phone) => {
    setDeletePhone(phone)
  }

  const confirmDelete = () => {
    if (deletePhone) {
      deleteMutation.mutate(deletePhone._id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-900">Phone Numbers</h1>
          <p className="mt-1 text-sm text-primary-600">
            Manage your Jambonz phone numbers and routing
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-3">
          {user?.role === 'admin' && (
            <>
              <button
                type="button"
                onClick={() => syncMutation.mutate()}
                disabled={syncMutation.isLoading}
                className="btn-secondary"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {syncMutation.isLoading ? 'Syncing...' : 'Sync Data'}
              </button>
              <button
                type="button"
                onClick={handleCreate}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Phone Number
              </button>
            </>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-primary-400" />
          </div>
          <input
            type="text"
            placeholder="Search phone numbers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Phone Numbers Table */}
      {filteredPhones.length === 0 ? (
        <div className="text-center py-12">
          <Phone className="mx-auto h-12 w-12 text-primary-400" />
          <h3 className="mt-2 text-sm font-medium text-primary-900">No phone numbers</h3>
          <p className="mt-1 text-sm text-primary-500">
            {searchTerm ? 'No phone numbers match your search.' : 'Get started by adding a phone number.'}
          </p>
          {user?.role === 'admin' && !searchTerm && (
            <div className="mt-6">
              <button
                type="button"
                onClick={handleCreate}
                className="btn-primary"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Phone Number
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-primary-200">
              <thead className="bg-primary-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Phone Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Carrier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Application
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-primary-500 uppercase tracking-wider">
                    Last Synced
                  </th>
                  {user?.role === 'admin' && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-primary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-primary-200">
                {filteredPhones.map((phone) => (
                  <tr key={phone._id} className="hover:bg-primary-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                            <Phone className="h-4 w-4 text-green-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary-900">
                            {phone.phoneNumber}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-500">
                      {phone.carrierName || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-500">
                      {phone.applicationName || 'No app assigned'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`status-badge ${phone.isActive ? 'status-active' : 'status-inactive'}`}>
                        {phone.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-500">
                      {phone.lastSyncedAt ? new Date(phone.lastSyncedAt).toLocaleDateString() : 'Never'}
                    </td>
                    {user?.role === 'admin' && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(phone)}
                          className="text-primary-600 hover:text-primary-900 mr-3"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(phone)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modals */}
      <PhoneNumberModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        phoneNumber={selectedPhone}
      />

      <ConfirmDialog
        isOpen={!!deletePhone}
        onClose={() => setDeletePhone(null)}
        onConfirm={confirmDelete}
        title="Delete Phone Number"
        message={`Are you sure you want to delete "${deletePhone?.phoneNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="btn-danger"
      />
    </div>
  )
}

export default PhoneNumbers